import React, { useMemo, useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { formatARS } from '../../utils/formatCurrency';

export interface ReconciliationFact {
  kind: 'compra' | 'entrega' | 'factura';
  date: string;
  productKey: string;
  partyId: number;
  partyName: string;
  docId: number;
  doc: string;
  bags: number;
  amount: number;
  stock: boolean;
}

export interface ReconciliationData {
  facts: ReconciliationFact[];
  products: Array<{ productKey: string; productName: string; productColor: string }>;
  stock: Array<{ productKey: string; current: number; adjustments: number }>;
  undetailedDocuments: Array<{ id: number; date: string; partyId: number; partyName: string; category: string; amount: number; note: string }>;
}

const PACK_SIZE = 50;
const KIND_LABEL: Record<ReconciliationFact['kind'], string> = { compra: 'Remito proveedor', entrega: 'Remito a cliente', factura: 'Factura' };

const fmtNum = (n: number) => n.toLocaleString('es-AR');
const formatDMY = (iso: string) => { const [y, m, d] = String(iso || '').slice(0, 10).split('-'); return d ? `${d}/${m}/${y}` : '-'; };

const Bags: React.FC<{ value: number; tone?: string }> = ({ value, tone = '' }) => (
  <span className={tone}>
    {value ? fmtNum(value) : '-'}
    {value !== 0 && <span className="block text-[11px] font-normal text-gray-400">{fmtNum(Math.round((value / PACK_SIZE) * 100) / 100)} packs</span>}
  </span>
);

interface ClientRow { clientId: number; clientName: string; delivered: number; invoiced: number; diff: number; estimate: number; docs: ReconciliationFact[]; }
interface ArticleRow { key: string; name: string; purchased: number; purchasedStock: number; delivered: number; deliveredStock: number; invoiced: number; diff: number; estimate: number; clients: ClientRow[]; purchases: ReconciliationFact[]; stockGap: number | null; current: number | null; }

// Bags delivered without invoice valued at the remito sale price; falls back to the invoiced price per bag.
const estimateValue = (docs: ReconciliationFact[], diff: number) => {
  const priced = (kind: ReconciliationFact['kind']) => docs.filter((d) => d.kind === kind && d.amount > 0);
  const avg = (list: ReconciliationFact[]) => { const bags = list.reduce((s, d) => s + d.bags, 0); return bags ? list.reduce((s, d) => s + d.amount, 0) / bags : 0; };
  const unit = avg(priced('entrega')) || avg(priced('factura'));
  return diff * unit;
};

const ArticleReconciliation: React.FC<{ data: ReconciliationData }> = ({ data }) => {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [clientId, setClientId] = useState('');
  const [search, setSearch] = useState('');
  const [onlyDiff, setOnlyDiff] = useState(true);
  const [openArticle, setOpenArticle] = useState<string | null>(null);
  const [openClient, setOpenClient] = useState<string | null>(null);

  const clients = useMemo(() => {
    const map = new Map<number, string>();
    data.facts.filter((f) => f.kind !== 'compra').forEach((f) => map.set(f.partyId, f.partyName));
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [data.facts]);

  const wholeHistory = !from && !to && !clientId;

  const articles = useMemo<ArticleRow[]>(() => {
    const names = new Map(data.products.map((p) => [p.productKey, p.productName]));
    const stock = new Map(data.stock.map((s) => [s.productKey, s]));
    const inRange = (f: ReconciliationFact) => (!from || f.date.slice(0, 7) >= from) && (!to || f.date.slice(0, 7) <= to);
    const facts = data.facts.filter((f) => inRange(f) && (!clientId || f.kind === 'compra' || String(f.partyId) === clientId));
    const byKey = new Map<string, ReconciliationFact[]>();
    facts.forEach((f) => { const list = byKey.get(f.productKey) || []; list.push(f); byKey.set(f.productKey, list); });
    const sum = (list: ReconciliationFact[], kind: ReconciliationFact['kind'], onlyStock = false) => list.filter((f) => f.kind === kind && (!onlyStock || f.stock)).reduce((s, f) => s + f.bags, 0);
    return Array.from(byKey, ([key, list]) => {
      const byClient = new Map<number, ReconciliationFact[]>();
      list.filter((f) => f.kind !== 'compra').forEach((f) => { const l = byClient.get(f.partyId) || []; l.push(f); byClient.set(f.partyId, l); });
      const clientRows: ClientRow[] = Array.from(byClient, ([id, docs]) => {
        const delivered = sum(docs, 'entrega'); const invoiced = sum(docs, 'factura'); const diff = delivered - invoiced;
        return { clientId: id, clientName: docs[0].partyName, delivered, invoiced, diff, estimate: estimateValue(docs, diff), docs: [...docs].sort((a, b) => a.date.localeCompare(b.date)) };
      }).sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff));
      const purchasedStock = sum(list, 'compra', true); const deliveredStock = sum(list, 'entrega', true);
      const s = stock.get(key);
      const delivered = sum(list, 'entrega'); const invoiced = sum(list, 'factura');
      return {
        key, name: names.get(key) || key, purchased: sum(list, 'compra'), purchasedStock, delivered, deliveredStock, invoiced,
        diff: delivered - invoiced, estimate: clientRows.reduce((acc, c) => acc + c.estimate, 0), clients: clientRows,
        purchases: list.filter((f) => f.kind === 'compra').sort((a, b) => a.date.localeCompare(b.date)),
        current: s ? s.current : (wholeHistory ? 0 : null),
        // Bought into stock − delivered from stock ± manual adjustments should equal what is on the shelf.
        stockGap: wholeHistory ? purchasedStock - deliveredStock + (s?.adjustments || 0) - (s?.current || 0) : null,
      };
    }).sort((a, b) => a.name.localeCompare(b.name));
  }, [data, from, to, clientId, wholeHistory]);

  const term = search.trim().toLowerCase();
  const visible = articles.filter((a) => (!term || a.name.toLowerCase().includes(term)) && (!onlyDiff || a.diff !== 0 || (a.stockGap || 0) !== 0));
  const totals = visible.reduce((t, a) => ({
    purchased: t.purchased + a.purchased, delivered: t.delivered + a.delivered, invoiced: t.invoiced + a.invoiced,
    notInvoiced: t.notInvoiced + a.clients.filter((c) => c.diff > 0).reduce((s, c) => s + c.diff, 0),
    notInvoicedValue: t.notInvoicedValue + a.clients.filter((c) => c.diff > 0).reduce((s, c) => s + c.estimate, 0),
    overInvoiced: t.overInvoiced + a.clients.filter((c) => c.diff < 0).reduce((s, c) => s - c.diff, 0),
  }), { purchased: 0, delivered: 0, invoiced: 0, notInvoiced: 0, notInvoicedValue: 0, overInvoiced: 0 });
  const undetailed = data.undetailedDocuments.filter((d) => (!clientId || String(d.partyId) === clientId) && (!from || d.date.slice(0, 7) >= from) && (!to || d.date.slice(0, 7) <= to));

  const diffTone = (n: number) => (n > 0 ? 'font-semibold text-amber-700' : n < 0 ? 'font-semibold text-red-700' : 'text-gray-400');

  return (
    <section className="space-y-4 rounded-xl border border-gray-200 bg-white p-3 shadow-sm sm:p-4">
      <div>
        <h2 className="font-semibold text-gray-900">Control por artículo: comprado, entregado y facturado</h2>
        <p className="mt-1 text-xs text-gray-600">Cruza los remitos de proveedor, los remitos a clientes y las facturas cargadas en cuenta corriente para encontrar bolsas entregadas sin facturar, facturadas sin entregar o que faltan en el stock. Tocá un artículo para ver cada cliente, y un cliente para ver los remitos y facturas.</p>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <label className="text-xs font-medium text-gray-600">Desde<input type="month" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1 block w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm" /></label>
        <label className="text-xs font-medium text-gray-600">Hasta<input type="month" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 block w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm" /></label>
        <label className="text-xs font-medium text-gray-600">Cliente
          <select value={clientId} onChange={(e) => setClientId(e.target.value)} className="mt-1 block w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm">
            <option value="">Todos</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <label className="text-xs font-medium text-gray-600">Artículo<input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Ej: 60 x 90" className="mt-1 block w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm" /></label>
      </div>
      <div className="flex flex-wrap items-center gap-4 text-xs text-gray-600">
        <label className="flex items-center gap-2"><input type="checkbox" checked={onlyDiff} onChange={(e) => setOnlyDiff(e.target.checked)} />Solo artículos con diferencias</label>
        {(from || to || clientId || search) && <button type="button" onClick={() => { setFrom(''); setTo(''); setClientId(''); setSearch(''); }} className="text-primary-600 hover:underline">Limpiar filtros</button>}
      </div>

      <div className="grid grid-cols-2 gap-2 lg:grid-cols-5">
        <div className="rounded-lg border p-3"><p className="text-xs text-gray-500">Comprado a proveedores</p><p className="font-bold text-gray-900">{fmtNum(totals.purchased)} bolsas</p></div>
        <div className="rounded-lg border p-3"><p className="text-xs text-gray-500">Entregado a clientes</p><p className="font-bold text-gray-900">{fmtNum(totals.delivered)} bolsas</p></div>
        <div className="rounded-lg border p-3"><p className="text-xs text-gray-500">Facturado a clientes</p><p className="font-bold text-gray-900">{fmtNum(totals.invoiced)} bolsas</p></div>
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3"><p className="text-xs text-amber-800">Entregado sin facturar</p><p className="font-bold text-amber-800">{fmtNum(totals.notInvoiced)} bolsas</p>{totals.notInvoicedValue > 0 && <p className="text-[11px] text-amber-700">≈ {formatARS(totals.notInvoicedValue)} sin IVA</p>}</div>
        <div className="rounded-lg border border-red-200 bg-red-50 p-3"><p className="text-xs text-red-800">Facturado sin entregar</p><p className="font-bold text-red-700">{fmtNum(totals.overInvoiced)} bolsas</p></div>
      </div>

      <div className="overflow-x-auto rounded-lg border">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="px-3 py-3 sm:px-4">Artículo</th>
              <th className="px-3 py-3 text-right sm:px-4">Comprado</th>
              <th className="px-3 py-3 text-right sm:px-4">Entregado</th>
              <th className="px-3 py-3 text-right sm:px-4">Facturado</th>
              <th className="px-3 py-3 text-right sm:px-4">Entregado − facturado</th>
              {wholeHistory && <th className="px-3 py-3 text-right sm:px-4">Stock actual</th>}
              {wholeHistory && <th className="px-3 py-3 text-right sm:px-4">Faltante de stock</th>}
            </tr>
          </thead>
          <tbody className="divide-y">
            {visible.map((a) => (
              <React.Fragment key={a.key}>
                <tr className="cursor-pointer hover:bg-gray-50" onClick={() => { setOpenArticle(openArticle === a.key ? null : a.key); setOpenClient(null); }}>
                  <td className="whitespace-nowrap px-3 py-2.5 font-medium sm:px-4"><span className="inline-flex items-center gap-1">{openArticle === a.key ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}{a.name}</span></td>
                  <td className="px-3 py-2.5 text-right sm:px-4"><Bags value={a.purchased} /></td>
                  <td className="px-3 py-2.5 text-right sm:px-4"><Bags value={a.delivered} /></td>
                  <td className="px-3 py-2.5 text-right sm:px-4"><Bags value={a.invoiced} /></td>
                  <td className="px-3 py-2.5 text-right sm:px-4"><Bags value={a.diff} tone={diffTone(a.diff)} />{a.estimate > 0 && <span className="block text-[11px] text-amber-700">≈ {formatARS(a.estimate)}</span>}</td>
                  {wholeHistory && <td className="px-3 py-2.5 text-right sm:px-4"><Bags value={a.current || 0} /></td>}
                  {wholeHistory && <td className="px-3 py-2.5 text-right sm:px-4"><Bags value={a.stockGap || 0} tone={diffTone(a.stockGap || 0)} /></td>}
                </tr>
                {openArticle === a.key && (
                  <tr><td colSpan={wholeHistory ? 7 : 5} className="bg-gray-50 px-3 py-3 sm:px-4">
                    <div className="space-y-3">
                      {a.clients.length === 0 ? <p className="text-xs text-gray-500">Sin entregas ni facturas en este período.</p> : (
                        <table className="min-w-full rounded border bg-white text-xs">
                          <thead className="text-left text-gray-500"><tr><th className="px-3 py-2">Cliente</th><th className="px-3 py-2 text-right">Entregado</th><th className="px-3 py-2 text-right">Facturado</th><th className="px-3 py-2 text-right">Diferencia</th><th className="px-3 py-2">Qué revisar</th></tr></thead>
                          <tbody className="divide-y">
                            {a.clients.map((c) => {
                              const rowKey = `${a.key}-${c.clientId}`;
                              return (
                                <React.Fragment key={rowKey}>
                                  <tr className="cursor-pointer hover:bg-gray-50" onClick={() => setOpenClient(openClient === rowKey ? null : rowKey)}>
                                    <td className="px-3 py-2 font-medium"><span className="inline-flex items-center gap-1">{openClient === rowKey ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}{c.clientName}</span></td>
                                    <td className="px-3 py-2 text-right">{fmtNum(c.delivered)}</td>
                                    <td className="px-3 py-2 text-right">{fmtNum(c.invoiced)}</td>
                                    <td className={`px-3 py-2 text-right ${diffTone(c.diff)}`}>{c.diff ? fmtNum(c.diff) : 'OK'}</td>
                                    <td className="px-3 py-2 text-gray-600">{c.diff > 0 ? `Falta facturar ${fmtNum(c.diff)} bolsas${c.estimate > 0 ? ` (≈ ${formatARS(c.estimate)} sin IVA)` : ''}` : c.diff < 0 ? `Se facturaron ${fmtNum(-c.diff)} bolsas más de las entregadas` : 'Coincide'}</td>
                                  </tr>
                                  {openClient === rowKey && (
                                    <tr><td colSpan={5} className="bg-gray-50 px-3 py-2">
                                      <div className="space-y-1">
                                        {c.docs.map((d, i) => (
                                          <div key={`${d.kind}-${d.docId}-${i}`} className="flex flex-wrap justify-between gap-2 rounded border bg-white px-3 py-1.5">
                                            <span><span className={`mr-2 rounded-full px-2 py-0.5 font-semibold ${d.kind === 'entrega' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>{KIND_LABEL[d.kind]}</span>{formatDMY(d.date)} · N° {d.doc || 's/n'}{d.kind === 'entrega' && !d.stock ? ' · no descuenta stock propio' : ''}</span>
                                            <span className="font-semibold">{fmtNum(d.bags)} bolsas{d.amount > 0 ? ` · ${formatARS(d.amount)} sin IVA` : ''}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </td></tr>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                      {a.purchases.length > 0 && (
                        <div>
                          <p className="mb-1 text-xs font-semibold text-gray-700">Compras a proveedores</p>
                          <div className="space-y-1">
                            {a.purchases.map((d, i) => (
                              <div key={`${d.docId}-${i}`} className="flex flex-wrap justify-between gap-2 rounded border bg-white px-3 py-1.5 text-xs">
                                <span><span className="mr-2 rounded-full bg-gray-100 px-2 py-0.5 font-semibold text-gray-700">{KIND_LABEL.compra}</span>{formatDMY(d.date)} · {d.partyName} · N° {d.doc || 's/n'}{!d.stock ? ' · no carga stock' : ''}</span>
                                <span className="font-semibold">{fmtNum(d.bags)} bolsas{d.amount > 0 ? ` · ${formatARS(d.amount)}` : ''}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {wholeHistory && (a.stockGap || 0) !== 0 && (
                        <p className={`text-xs ${diffTone(a.stockGap || 0)}`}>
                          {(a.stockGap || 0) > 0
                            ? `Entraron ${fmtNum(a.purchasedStock)} bolsas al stock y salieron ${fmtNum(a.deliveredStock)} por remitos, pero en stock hay ${fmtNum(a.current || 0)}: faltan ${fmtNum(a.stockGap || 0)} bolsas (¿una entrega sin remito, o un ajuste que no se cargó?).`
                            : `En stock hay ${fmtNum(-(a.stockGap || 0))} bolsas más de las que explican las compras, los remitos y los ajustes (¿una compra sin remito de proveedor?).`}
                        </p>
                      )}
                    </div>
                  </td></tr>
                )}
              </React.Fragment>
            ))}
            {!visible.length && <tr><td colSpan={wholeHistory ? 7 : 5} className="px-4 py-8 text-center text-gray-500">{onlyDiff ? 'No hay diferencias para estos filtros. Desmarcá "Solo artículos con diferencias" para ver todo.' : 'No hay movimientos para estos filtros.'}</td></tr>}
          </tbody>
        </table>
      </div>

      {undetailed.length > 0 && (
        <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="min-w-0">
            <p><strong>{undetailed.length} documento{undetailed.length === 1 ? '' : 's'} de clientes sin detalle de artículos</strong> por {formatARS(undetailed.reduce((s, d) => s + d.amount, 0))}. No se pueden cruzar por artículo: si son facturas, cargales los artículos en la cuenta corriente para que el control sea exacto.</p>
            <ul className="mt-1 space-y-0.5">
              {undetailed.slice(0, 8).map((d) => <li key={d.id}>{formatDMY(d.date)} · {d.partyName} · {d.category} · {formatARS(d.amount)}{d.note ? ` · ${d.note}` : ''}</li>)}
            </ul>
            {undetailed.length > 8 && <p className="mt-1">Y {undetailed.length - 8} más.</p>}
          </div>
        </div>
      )}

      <p className="text-xs text-gray-500">Las bolsas se agrupan por medida y color, sin micronaje. Las fechas son las del remito o la factura. Con un rango de meses, las diferencias pueden venir de una entrega facturada en otro mes. "Faltante de stock" solo se calcula sobre toda la historia y sin filtrar por cliente. El valor estimado usa el precio cargado en los remitos del cliente o, si no hay, el precio por bolsa de sus facturas.</p>
    </section>
  );
};

export default ArticleReconciliation;
